import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAdminGuardian,
  IAdminGuardianDetail,
  IAdminGuardianInstitute,
  IAdminGuardianChildHierarchy,
  IGetAdminGuardiansResponse,
  IGetAdminGuardianChildrenResponse,
  IGuardianOrganizationOption,
  IGetAdminGuardianByIdResponse,
  IGetAdminGuardianInstitutesResponse,
  IDeactivateGuardianResponse,
  IUpdateAdminGuardianResponse,
  IUnlinkGuardianFromInstituteResponse,
} from '../../../interface/classveew-admin/admin-guardian.interface';
import { IGuardianSearchResult } from '../../../interface/guardian.interface';
import {
  fetchAdminGuardians,
  fetchAdminGuardianById,
  fetchAdminGuardianInstitutes,
  fetchAdminGuardianChildren,
  deactivateGuardian,
  reactivateGuardian,
  deleteGuardian,
  updateAdminGuardianProfile,
  unlinkGuardian,
  unlinkGuardianChild,
  createAdminGuardian,
  fetchGuardianOrganizationOptions,
  searchGuardian,
  linkGuardian,
  sendInvite,
  manualCreate,
} from '../../action/classveew-admin/admin-guardian.action';

interface IAdminGuardianState {
  guardians: IAdminGuardian[];
  selectedGuardian: IAdminGuardianDetail | null;
  guardianInstitutes: IAdminGuardianInstitute[];
  guardianChildren: IAdminGuardianChildHierarchy[];
  organizationOptions: IGuardianOrganizationOption[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  isLoading: boolean;
  searchResult: IGuardianSearchResult | null;
  isSearching: boolean;
  isLinking: boolean;
  isSendingInvite: boolean;
  isCreating: boolean;
  error: string | null;
}

const initialState: IAdminGuardianState = {
  guardians: [],
  selectedGuardian: null,
  guardianInstitutes: [],
  guardianChildren: [],
  organizationOptions: [],
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 0,
  isLoading: false,
  searchResult: null,
  isSearching: false,
  isLinking: false,
  isSendingInvite: false,
  isCreating: false,
  error: null,
};

const adminGuardianSlice = createSlice({
  name: 'adminGuardian',
  initialState,
  reducers: {
    clearAdminGuardianError: (state) => {
      state.error = null;
    },
    clearAdminGuardians: (state) => {
      state.guardians = [];
      state.page = 1;
      state.total = 0;
      state.totalPages = 0;
    },
    clearSelectedGuardian: (state) => {
      state.selectedGuardian = null;
      state.guardianInstitutes = [];
      state.guardianChildren = [];
    },
    clearGuardianInstitutes: (state) => {
      state.guardianInstitutes = [];
    },
    clearGuardianChildren: (state) => {
      state.guardianChildren = [];
    },
    clearSearchResult: (state) => {
      state.searchResult = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Admin Guardian Management
      .addCase(fetchAdminGuardians.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminGuardians.fulfilled,
        (state, action: PayloadAction<IGetAdminGuardiansResponse>) => {
          state.isLoading = false;
          state.guardians = action.payload.data?.guardians || [];
          state.page = action.payload.data?.page || 1;
          state.pageSize = action.payload.data?.pageSize || 20;
          state.total = action.payload.data?.total || 0;
          state.totalPages = action.payload.data?.totalPages || 0;
          state.error = null;
        },
      )
      .addCase(fetchAdminGuardians.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch guardians';
        state.guardians = [];
      })
      .addCase(fetchAdminGuardianById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminGuardianById.fulfilled,
        (state, action: PayloadAction<IGetAdminGuardianByIdResponse>) => {
          state.isLoading = false;
          state.selectedGuardian = action.payload.data?.guardian || null;
          state.guardianInstitutes = action.payload.data?.guardian?.institutes || [];
          state.error = null;
        },
      )
      .addCase(fetchAdminGuardianById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch guardian';
        state.selectedGuardian = null;
      })
      .addCase(updateAdminGuardianProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateAdminGuardianProfile.fulfilled,
        (state, action: PayloadAction<IUpdateAdminGuardianResponse>) => {
          state.isLoading = false;
          const updatedGuardian = action.payload.data?.guardian;
          if (updatedGuardian) {
            state.selectedGuardian = updatedGuardian;
            state.guardians = state.guardians.map((guardian) =>
              guardian.id === updatedGuardian.id
                ? {
                    ...guardian,
                    first_name: updatedGuardian.first_name,
                    last_name: updatedGuardian.last_name,
                    email: updatedGuardian.email,
                    contact_no: updatedGuardian.contact_no,
                    is_active: updatedGuardian.is_active,
                  }
                : guardian,
            );
          }
          state.error = null;
        },
      )
      .addCase(updateAdminGuardianProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to update guardian profile';
      })
      .addCase(fetchAdminGuardianInstitutes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminGuardianInstitutes.fulfilled,
        (state, action: PayloadAction<IGetAdminGuardianInstitutesResponse>) => {
          state.isLoading = false;
          state.guardianInstitutes = action.payload.data?.organizations || [];
          if (state.selectedGuardian) {
            state.selectedGuardian.institutes = action.payload.data?.organizations || [];
          }
          state.error = null;
        },
      )
      .addCase(fetchAdminGuardianInstitutes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch guardian institutes';
      })
      .addCase(deactivateGuardian.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        deactivateGuardian.fulfilled,
        (state, action: PayloadAction<IDeactivateGuardianResponse>) => {
          state.isLoading = false;
          const updatedGuardian = action.payload.data?.guardian;
          if (updatedGuardian) {
            state.selectedGuardian = updatedGuardian;
            state.guardians = state.guardians.map((guardian) =>
              guardian.id === updatedGuardian.id
                ? {
                    ...guardian,
                    is_active: updatedGuardian.is_active,
                  }
                : guardian,
            );
          }
          state.error = null;
        },
      )
      .addCase(deactivateGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to deactivate guardian';
      })
      .addCase(reactivateGuardian.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        reactivateGuardian.fulfilled,
        (state, action: PayloadAction<IDeactivateGuardianResponse>) => {
          state.isLoading = false;
          const updatedGuardian = action.payload.data?.guardian;
          if (updatedGuardian) {
            state.selectedGuardian = updatedGuardian;
            state.guardians = state.guardians.map((guardian) =>
              guardian.id === updatedGuardian.id
                ? {
                    ...guardian,
                    is_active: updatedGuardian.is_active,
                  }
                : guardian,
            );
          }
          state.error = null;
        },
      )
      .addCase(reactivateGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to reactivate guardian';
      })
      .addCase(deleteGuardian.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteGuardian.fulfilled, (state) => {
        state.isLoading = false;
        // Remove deleted guardian from list
        if (state.selectedGuardian?.id) {
          state.guardians = state.guardians.filter((g) => g.id !== state.selectedGuardian?.id);
          state.selectedGuardian = null;
        }
        state.error = null;
      })
      .addCase(deleteGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to delete guardian';
      })
      .addCase(unlinkGuardian.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        unlinkGuardian.fulfilled,
        (state, action: PayloadAction<IUnlinkGuardianFromInstituteResponse>) => {
          state.isLoading = false;
          const { organization_id } = action.payload.data;
          state.guardianInstitutes = state.guardianInstitutes.filter(
            (item) => item.id !== organization_id,
          );
          if (state.selectedGuardian?.institutes) {
            state.selectedGuardian.institutes = state.selectedGuardian.institutes.filter(
              (item) => item.id !== organization_id,
            );
          }
          state.error = null;
        },
      )
      .addCase(unlinkGuardian.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchAdminGuardianChildren.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminGuardianChildren.fulfilled,
        (state, action: PayloadAction<IGetAdminGuardianChildrenResponse>) => {
          state.isLoading = false;
          state.guardianChildren = action.payload.data?.hierarchy || [];
          state.error = null;
        },
      )
      .addCase(fetchAdminGuardianChildren.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch guardian children';
      })
      .addCase(unlinkGuardianChild.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(unlinkGuardianChild.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(unlinkGuardianChild.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to unlink child';
      })
      .addCase(createAdminGuardian.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAdminGuardian.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createAdminGuardian.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchGuardianOrganizationOptions.fulfilled, (state, action) => {
        state.organizationOptions = action.payload;
      })

      // Guardian Onboarding (Search, Link, Send Invite, Manual Create)
      .addCase(searchGuardian.pending, (state) => {
        state.isSearching = true;
        state.error = null;
        state.searchResult = null;
      })
      .addCase(searchGuardian.fulfilled, (state, action: PayloadAction<IGuardianSearchResult>) => {
        state.isSearching = false;
        state.searchResult = action.payload;
        state.error = null;
      })
      .addCase(searchGuardian.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload?.message || 'Failed to search guardian';
        state.searchResult = null;
      })

      // Link Guardian
      .addCase(linkGuardian.pending, (state) => {
        state.isLinking = true;
        state.error = null;
      })
      .addCase(linkGuardian.fulfilled, (state) => {
        state.isLinking = false;
        state.error = null;
      })
      .addCase(linkGuardian.rejected, (state, action) => {
        state.isLinking = false;
        state.error = action.payload?.message || 'Failed to link guardian';
      })

      // Send Invite
      .addCase(sendInvite.pending, (state) => {
        state.isSendingInvite = true;
        state.error = null;
      })
      .addCase(sendInvite.fulfilled, (state) => {
        state.isSendingInvite = false;
        state.error = null;
      })
      .addCase(sendInvite.rejected, (state, action) => {
        state.isSendingInvite = false;
        state.error = action.payload?.message || 'Failed to send invite';
      })

      // Manual Create
      .addCase(manualCreate.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(manualCreate.fulfilled, (state) => {
        state.isCreating = false;
        state.error = null;
      })
      .addCase(manualCreate.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload?.message || 'Failed to create guardian';
      });
  },
});

export const {
  clearAdminGuardianError,
  clearAdminGuardians,
  clearSelectedGuardian,
  clearGuardianInstitutes,
  clearGuardianChildren,
  clearSearchResult,
  clearError,
} = adminGuardianSlice.actions;

export default adminGuardianSlice.reducer;
